import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useGlobalStore } from '@/stores';
import {
  Address,
  BpOrderLine,
  BpOrdersResponse,
  BusinessPartner,
  BusinessPartnerWithStats,
  SalesPerson,
  ShippingType,
} from '@/api/business-partner/business-partner.types.ts';
import { useMemo } from 'react';
import { businessPartnersApi } from '@/api';
import { ContactEmployee, ContactEmployeeWithUser } from '@/api/contact-employee/contact-employee.types.ts';
import { Item } from '@/api/items/items.types.ts';
import { queryKeys } from '../query-keys';

export const useGetAddressesByBusinessPartnerId = (
  id?: number,
  options?: Partial<UseQueryOptions<Address[], AxiosError>>,
) => {
  const store = useGlobalStore();
  let businessPartnerId = store.businessPartnerId as number;
  if (id != null) {
    businessPartnerId = id;
  }
  return useQuery<Address[], AxiosError>({
    queryKey: ['businessPartnerAddresses', businessPartnerId],
    queryFn: () =>
      businessPartnersApi
        .getAddressesByBusinessPartnerId(businessPartnerId)
        .then((res) => res.data),
    ...options,
  });
};

export const useGetAllBusinessPartners = (
  options?: UseQueryOptions<BusinessPartner[], AxiosError>,
) => {
  return useQuery<BusinessPartner[], AxiosError>({
    queryKey: ['businessPartners'],
    queryFn: () =>
      businessPartnersApi.getAllBusinessPartners().then((res) => res.data),
    ...options,
  });
};

export const useGetAllBusinessPartnersWithOrderStats = () => {
  return useQuery<BusinessPartnerWithStats[], AxiosError>({
    queryKey: ['businessPartnersWithOrderStats'],
    queryFn: () =>
      businessPartnersApi
        .getAllBusinessPartnersWithOrderStats()
        .then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetOrdersByBusinessPartnerId = (
  bpId?: number,
  limit = 50,
  offset = 0,
  options?: Partial<UseQueryOptions<BpOrdersResponse, AxiosError>>,
) => {
  return useQuery<BpOrdersResponse, AxiosError>({
    queryKey: ['bpOrders', bpId, limit, offset],
    queryFn: () =>
      businessPartnersApi
        .getOrdersByBusinessPartnerId(bpId!, limit, offset)
        .then((res) => res.data),
    enabled: !!bpId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useGetAllContactEmployees = (
  options?: UseQueryOptions<ContactEmployee[], AxiosError>,
) => {
  return useQuery<ContactEmployee[], AxiosError>({
    queryKey: ['contactEmployees'],
    queryFn: () =>
      businessPartnersApi.getAllContactEmployees().then((res) => res.data),
    ...options,
  });
};

export const useGetBusinessPartnerById = (
  id?: number,
  options?: UseQueryOptions<BusinessPartner, AxiosError>,
) => {
  const store = useGlobalStore();
  let businessPartnerId = store.businessPartnerId as number;
  if (id != null) {
    businessPartnerId = id;
  }
  return useQuery<BusinessPartner, AxiosError>({
    queryKey: ['businessPartner', businessPartnerId],
    queryFn: () =>
      businessPartnersApi
        .getBusinessPartnerById(businessPartnerId)
        .then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useGetContactEmployeesByBusinessPartnerId = (
  id?: number,
  options?: Partial<UseQueryOptions<ContactEmployee[], AxiosError>>,
) => {
  const store = useGlobalStore();
  let businessPartnerId = store.businessPartnerId as number;
  if (id != null) {
    businessPartnerId = id;
  }
  return useQuery<ContactEmployee[], AxiosError>({
    queryKey: ['contactEmployees', businessPartnerId],
    queryFn: () =>
      businessPartnersApi
        .getContactEmployeesByBusinessPartnerId(businessPartnerId)
        .then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useGetOrderLinesByDocEntry = (
  businessPartnerId: number,
  docEntry: number,
  options?: Partial<UseQueryOptions<BpOrderLine[], AxiosError>>,
) => {
  return useQuery<BpOrderLine[], AxiosError>({
    queryKey: ['orderLines', businessPartnerId, docEntry],
    queryFn: () =>
      businessPartnersApi
        .getOrderLinesByDocEntry(businessPartnerId, docEntry)
        .then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useGetContactEmployeesWithUserByBusinessPartnerId = (
  id?: number,
  options?: Partial<UseQueryOptions<ContactEmployeeWithUser[], AxiosError>>,
) => {
  const store = useGlobalStore();
  let businessPartnerId = store.businessPartnerId as number;
  if (id != null) {
    businessPartnerId = id;
  }
  return useQuery<ContactEmployeeWithUser[], AxiosError>({
    queryKey: ['contactEmployeesWithUser', businessPartnerId],
    queryFn: () =>
      businessPartnersApi
        .getContactEmployeesWithUserByBusinessPartnerId(businessPartnerId)
        .then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useGetItemsByBusinessPartnerId = (
  options?: UseQueryOptions<Item[], AxiosError>,
) => {
  const { businessPartnerId } = useGlobalStore();
  if (!businessPartnerId) {
    throw new Error('Business partner ID is not set');
  }
  return useQuery<Item[], AxiosError>({
    queryKey: queryKeys.businessPartnerItems(businessPartnerId),
    queryFn: () =>
      businessPartnersApi
        .getItemsByBusinessPartnerId(businessPartnerId)
        .then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useGetSalesPersonByBusinessPartnerId = (
  id?: number,
  options?: UseQueryOptions<SalesPerson | null, AxiosError>,
) => {
  const store = useGlobalStore();
  let businessPartnerId = store.businessPartnerId as number;
  if (id != null) {
    businessPartnerId = id;
  }
  return useQuery({
    queryKey: ['sales-person', businessPartnerId],
    queryFn: () =>
      businessPartnersApi
        .getSalesPersonByBusinessPartnerId(businessPartnerId)
        .then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetShippingTypesByBusinessPartnerId = (
  id?: number,
  options?: UseQueryOptions<ShippingType[], AxiosError>,
) => {
  const store = useGlobalStore();
  let businessPartnerId = store.businessPartnerId as number;
  if (id != null) {
    businessPartnerId = id;
  }
  return useQuery<ShippingType[], AxiosError>({
    queryKey: ['businessPartnerShippingTypes', businessPartnerId],
    queryFn: () =>
      businessPartnersApi
        .getShippingTypesByBusinessPartnerId(businessPartnerId)
        .then((res) => res.data),
    ...options,
  });
};

export const useSyncBusinessPartners = (
  options?: UseMutationOptions<void, AxiosError, void>,
) => {
  return useMutation<void, AxiosError, void>({
    mutationFn: () =>
      businessPartnersApi.syncBusinessPartners().then(() => undefined),
    ...options,
  });
};

export function useDefaultShippingAddress() {
  const { data: addresses = [], isPending: isAddressesPending } =
    useGetAddressesByBusinessPartnerId();
  const { data: businessPartner, isPending: isBusinessPartnerPending } =
    useGetBusinessPartnerById();

  const defaultShippingAddress = useMemo(
    () =>
      addresses?.find(
        (address) => address.id === businessPartner?.defaultShippingAddressId,
      ),
    [addresses, businessPartner],
  );

  return {
    defaultShippingAddress,
    isPending: isAddressesPending || isBusinessPartnerPending,
  };
}

export function useDefaultBillingAddress() {
  const { data: addresses = [], isPending: isAddressesPending } =
    useGetAddressesByBusinessPartnerId();
  const { data: businessPartner, isPending: isBusinessPartnerPending } =
    useGetBusinessPartnerById();

  const defaultBillingAddress = useMemo(
    () =>
      addresses?.find(
        (address) => address.id === businessPartner?.defaultBillingAddressId,
      ),
    [addresses, businessPartner],
  );

  return {
    defaultBillingAddress,
    isPending: isAddressesPending || isBusinessPartnerPending,
  };
}
